// =============================================================================
// apps/web/layout - chatbot-panel.component.ts
// Purpose: Wrapper around the ui-kit <ams-chatbot> that connects it to
//          ChatbotService (HTTP), ChatbotPanelStateService (open/closed),
//          and ChatbotPageContextService (per-page entity context).
//          Mounted once in the admin shell so the panel is reachable from
//          every page.
// =============================================================================

import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import {
  ChatbotComponent,
  ChatMessage,
  ChatNavigation,
  ChatSession,
  ChatSlotChipToggle,
  ChatSuggestion,
  ChatUsage,
} from '@ams/ui-kit';
import { ChatbotPanelStateService } from '../../services/chatbot-panel-state.service';
import {
  ChatbotService,
  ChatbotMessageRequest,
  ChatbotMessageResponse,
  ChatbotSessionSummary,
  ChatbotStoredMessage,
  ChatbotModuleCapabilities,
  ChatbotUsage,
} from '../../services/chatbot.service';
import { ChatbotPageContextService } from '../../services/chatbot-page-context.service';
import {
  ChatbotPrefillService,
  ChatbotPrefillData,
  ChatbotPrefillValue,
} from '../../services/chatbot-prefill.service';
import { type ChatbotPrefillKindCode } from '../../constants';

// Mirrors MemboraApiConstants.AiAssistant.ChatbotMaxSessionsPerUser on the
// backend. Drives the "history full" banner shown in the history pane.
const CHATBOT_HISTORY_LIMIT = 50;

@Component({
  selector: 'app-chatbot-panel',
  standalone: true,
  imports: [CommonModule, ChatbotComponent],
  template: `
    <ams-chatbot
      [open]="panelState.isOpen()"
      [emptySubtitle]="emptySubtitle()"
      [messages]="messages()"
      [history]="history()"
      [historyLimit]="historyLimit"
      [isLoading]="isLoading()"
      [error]="error()"
      [contextLabel]="contextLabel()"
      [slotSelections]="slotSelections()"
      [usage]="usage()"
      [sendDisabledReason]="sendDisabledReason()"
      (closed)="onClosed()"
      (messageSubmitted)="onMessageSubmitted($event)"
      (suggestionSelected)="onSuggestionSelected($event)"
      (navigationSelected)="onNavigationSelected($event)"
      (slotChipToggled)="onSlotChipToggle($event)"
      (sessionSelected)="onSessionSelected($event)"
      (sessionDeleted)="onSessionDeleted($event)"
      (newChatRequested)="onNewChatRequested()"
      (retryRequested)="onRetryRequested()"
    />
  `,
})
export class ChatbotPanelComponent implements OnDestroy {
  protected readonly panelState = inject(ChatbotPanelStateService);
  private readonly chatbotService = inject(ChatbotService);
  private readonly pageContext = inject(ChatbotPageContextService);
  private readonly prefillService = inject(ChatbotPrefillService);
  private readonly router = inject(Router);

  protected readonly historyLimit = CHATBOT_HISTORY_LIMIT;

  // Per-conversation state. New ConversationId is allocated by the
  // backend on the first message (we send null) and reused thereafter
  // so the planner sees turn history.
  private conversationId: string | null = null;

  // Last user message kept around so the retry banner can re-submit it
  // when persistence fails mid-turn. Cleared on success.
  private lastRequest: { content: string; userMessageId: string } | null = null;

  // Per-messageId in-flight selection PATCH subscriptions. A new chip toggle
  // for the same message cancels the previous PATCH before sending the next,
  // so out-of-order network completion can't overwrite the latest state with
  // a stale snapshot.
  private readonly inFlightSelectionPatches = new Map<string, Subscription>();

  protected readonly messages = signal<ChatMessage[]>([]);
  protected readonly history = signal<ChatSession[]>([]);
  protected readonly isLoading = signal(false);

  // Module capabilities for the empty-state message — fetched once on first open.
  private readonly capabilities = signal<ChatbotModuleCapabilities | null>(null);
  protected readonly emptySubtitle = computed(() => {
    const c = this.capabilities();
    if (!c) return 'Ask me anything about members, events, or association data.';
    const available = this.joinList(c.available);
    const inTraining = this.joinList(c.inTraining);
    const parts: string[] = [];
    if (available) parts.push(`You can ask me anything about ${available}.`);
    if (inTraining) parts.push(`I'm still being trained on ${inTraining}.`);
    return parts.join(' ');
  });
  protected readonly error = signal<string | null>(null);

  // AI credit + chat-session usage for the footer meter. Refreshed on open and
  // after each turn (a message burns credits; the first turn mints a session).
  protected readonly usage = signal<ChatUsage | null>(null);

  // Non-null when sending is currently blocked (out of AI credits / AI not in
  // plan) — disables the composer up front with this reason. Mirrors the
  // server-side send gate so the user can't type into a turn that would 403.
  protected readonly sendDisabledReason = signal<string | null>(null);
  // Per-message slot-fill chip selections. Outer key = messageId, inner key =
  // fieldKey (e.g. "capacityType"), value = selected option values. Multi-
  // select keeps array semantics; single-select holds at most one entry.
  // Clearing the array (or omitting the key) means nothing selected.
  protected readonly slotSelections = signal<Record<string, Record<string, string[]>>>({});

  // Fetch history whenever the panel opens. Lightweight payload (no
  // messages embedded) so this is cheap even at the cap. Also fires the
  // LLM warmup in parallel so a local model (Ollama / LM Studio) has time
  // to load into VRAM while the user types their first prompt — without
  // this, every cold-cache turn pays 10-30s of model-load latency.
  // Warmup is fire-and-forget; failures are non-fatal.
  private readonly _refreshHistoryOnOpen = effect(() => {
    if (this.panelState.isOpen()) {
      this.refreshHistory();
      // Fetch module capabilities once for the empty-state message.
      if (!this.capabilities()) {
        this.chatbotService.getCapabilities()
          .pipe(take(1))
          .subscribe({
            next: (caps) => this.capabilities.set(caps),
            error: () => { /* keep the default subtitle on failure */ },
          });
      }
      this.fetchUsage();
      this.chatbotService.warmupLlm()
        .pipe(take(1))
        .subscribe({
          next: () => {},
          error: (err: HttpErrorResponse) =>
            console.debug('[chatbot-panel] warmup failed (non-fatal):', err),
        });
    }
  });

  // Pull current usage for the meter. Soft-fail: leave the meter hidden (null)
  // if it can't load — it's an informational surface, not core chat.
  private fetchUsage(): void {
    this.chatbotService.getUsage()
      .pipe(take(1))
      .subscribe({
        next: (u: ChatbotUsage) => {
          this.usage.set({
            creditsUsed: u.credits.used,
            creditsCap: u.credits.cap,
            creditsRemaining: u.credits.remaining,
            creditsEnabled: u.credits.enabled,
            sessionsUsed: u.sessions.used,
            sessionsCap: u.sessions.cap,
          });
          this.sendDisabledReason.set(
            u.canSend ? null : (u.blockedReason ?? 'AI is unavailable right now.'),
          );
        },
        error: (err: HttpErrorResponse) =>
          console.warn('[chatbot-panel] usage fetch failed (non-fatal):', err),
      });
  }

  /** Label shown in the chatbot header so users know what context the
   *  assistant has. Null when no page has registered context. */
  protected readonly contextLabel = computed(() => {
    const ctx = this.pageContext.currentContext();
    if (!ctx) return null;
    const entityName = ctx.entityContext?.displayName;
    if (entityName) return entityName;
    return `${ctx.module} · ${ctx.variant ?? 'page'}`;
  });

  protected onClosed(): void {
    this.panelState.close();
  }

  /** "A", "A and B", "A, B, and C" — Oxford-comma join for the capability message. */
  private joinList(items: string[]): string {
    if (!items || items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  }

  protected onSuggestionSelected(suggestion: ChatSuggestion): void {
    this.onMessageSubmitted(suggestion.label);
  }

  protected onNavigationSelected(navigation: ChatNavigation): void {
    if (!navigation?.route) return;
    // Create-flow chips ship a kind + prefill; stage the payload in the
    // singleton service so the destination page can claim it via effect().
    // PII never appears in the URL because of this hand-off. Slot-fill chip
    // selections on the originating message are merged in here so they
    // travel into the dialog alongside LLM-extracted values.
    if (navigation.kind) {
      const merged = this.buildMergedPrefill(navigation);
      this.prefillService.request(
        navigation.kind as ChatbotPrefillKindCode,
        merged,
      );
    }
    this.router.navigateByUrl(navigation.route);
    this.panelState.close();
  }

  protected onSlotChipToggle(event: ChatSlotChipToggle): void {
    let nextMessageBag: Record<string, string[]> = {};
    this.slotSelections.update(state => {
      const messageBag = { ...(state[event.messageId] ?? {}) };
      const current = messageBag[event.fieldKey] ?? [];
      const isSelected = current.includes(event.value);

      let next: string[];
      if (event.mode === 'multi') {
        // Toggle independently.
        next = isSelected
          ? current.filter(v => v !== event.value)
          : [...current, event.value];
      } else {
        // Single-select: clicking the active chip clears the group; clicking
        // a different chip replaces.
        next = isSelected ? [] : [event.value];
      }

      if (next.length === 0) {
        delete messageBag[event.fieldKey];
      } else {
        messageBag[event.fieldKey] = next;
      }

      nextMessageBag = messageBag;
      return { ...state, [event.messageId]: messageBag };
    });

    // Persist the new selection snapshot for this message. Optimistic — the
    // chip already reflects the new state locally. Per-message cancellation
    // prevents out-of-order arrivals from overwriting newer state.
    this.persistSelections(event.messageId, nextMessageBag);
  }

  private persistSelections(messageId: string, selections: Record<string, string[]>): void {
    if (!this.conversationId) return; // No persisted session yet (shouldn't happen post-first-turn).

    const existing = this.inFlightSelectionPatches.get(messageId);
    if (existing) {
      existing.unsubscribe();
      this.inFlightSelectionPatches.delete(messageId);
    }

    const sub = this.chatbotService
      .updateMessageSelections(this.conversationId, messageId, selections)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.inFlightSelectionPatches.delete(messageId);
        },
        error: (err: HttpErrorResponse) => {
          this.inFlightSelectionPatches.delete(messageId);
          // Soft-fail: local state still reflects the click. Cross-device
          // sync just won't catch up until the next successful PATCH.
          console.warn('[chatbot-panel] persist selections failed:', err);
        },
      });
    this.inFlightSelectionPatches.set(messageId, sub);
  }

  ngOnDestroy(): void {
    for (const sub of this.inFlightSelectionPatches.values()) {
      sub.unsubscribe();
    }
    this.inFlightSelectionPatches.clear();
  }

  // Find the message that owns this nav chip and fold any local chip
  // selections into a copy of the chip's prefill. Chip selections override
  // LLM-extracted values for the same key (the user's explicit click wins).
  // Multi-select fields become string[]; single-select stays string.
  private buildMergedPrefill(navigation: ChatNavigation): ChatbotPrefillData {
    const base: ChatbotPrefillData = { ...(navigation.prefill ?? {}) };
    const owningMessage = this.messages().find(m => m.navigation?.includes(navigation));
    if (!owningMessage) return base;

    const selections = this.slotSelections()[owningMessage.id];
    if (!selections) return base;

    const groups = owningMessage.slotFillGroups ?? [];
    for (const group of groups) {
      const picked = selections[group.fieldKey];
      if (!picked || picked.length === 0) continue;
      const value: ChatbotPrefillValue = group.selectMode === 'multi' ? picked : picked[0];
      base[group.fieldKey] = value;
    }
    return base;
  }

  protected onMessageSubmitted(content: string): void {
    if (!content.trim() || this.isLoading()) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    this.messages.update(prev => [...prev, userMessage]);
    this.lastRequest = { content, userMessageId: userMessage.id };
    this.sendCurrentRequest(content);
  }

  protected onRetryRequested(): void {
    if (this.isLoading() || !this.lastRequest) return;
    // Re-send the same message. The user bubble is already in messages()
    // from the original submission; no need to add it again.
    this.sendCurrentRequest(this.lastRequest.content);
  }

  protected onNewChatRequested(): void {
    this.conversationId = null;
    this.lastRequest = null;
    this.messages.set([]);
    this.slotSelections.set({});
    this.error.set(null);
    this.refreshHistory();
  }

  protected onSessionSelected(session: ChatSession): void {
    if (this.isLoading()) return;
    this.error.set(null);
    this.lastRequest = null;
    this.chatbotService.getSessionMessages(session.id)
      .pipe(take(1))
      .subscribe({
        next: (stored) => {
          this.conversationId = session.id;
          this.messages.set(stored.map(this.hydrateStoredMessage));
          // Rebuild the per-message selections map from the stored
          // metadata. Each assistant turn that has a selections snapshot
          // contributes one entry; empty when none were ever clicked.
          const rebuilt: Record<string, Record<string, string[]>> = {};
          for (const row of stored) {
            const sel = row.metadata?.selections;
            if (sel && Object.keys(sel).length > 0) {
              rebuilt[row.id] = { ...sel };
            }
          }
          this.slotSelections.set(rebuilt);
        },
        error: (err: HttpErrorResponse) => {
          console.error('[chatbot-panel] load session failed:', err);
          this.error.set('Couldn\'t load that chat. Please try again.');
        },
      });
  }

  protected onSessionDeleted(session: ChatSession): void {
    if (!confirm('Delete this chat? This cannot be undone.')) return;
    this.chatbotService.deleteSession(session.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // If the active session was deleted, reset to a fresh chat.
          if (this.conversationId === session.id) {
            this.onNewChatRequested();
          } else {
            this.refreshHistory();
          }
          // Session count dropped — keep the meter in sync.
          this.fetchUsage();
        },
        error: (err: HttpErrorResponse) => {
          console.error('[chatbot-panel] delete session failed:', err);
          this.error.set('Couldn\'t delete that chat. Please try again.');
        },
      });
  }

  // Hydrate a stored DB row into the in-memory ChatMessage shape. Assistant
  // rows persisted post-V059 carry the response snapshot in metadata so we
  // can re-render chips/nav/suggestions exactly as the user saw them.
  // Pre-V059 rows hydrate without interactive elements; that's the
  // documented downgrade for legacy history.
  //
  // Role normalization: ai_message.role is persisted as "USER"/"ASSISTANT"
  // (per MemboraApiConstants.AiAssistant.MessageRole) but the in-memory
  // ChatRole union is lowercase. Without lowercasing here, the ui-kit
  // role check falls through both branches → bubbles render empty with
  // "You" labels.
  private hydrateStoredMessage = (stored: ChatbotStoredMessage): ChatMessage => {
    const role = (stored.role ?? '').toLowerCase() === 'assistant' ? 'assistant' : 'user';
    const meta = stored.metadata ?? undefined;
    return {
      id: stored.id,
      role,
      content: stored.content ?? '',
      timestamp: new Date(stored.createdAt),
      citations: meta?.citations ?? null,
      navigation: meta?.navigation ?? null,
      suggestions: meta?.suggestions?.map(label => ({ label, actionKey: null })) ?? null,
      slotFillGroups: meta?.slotFillGroups ?? null,
      toolRequired: meta?.toolRequired ?? false,
    };
  };

  private refreshHistory(): void {
    this.chatbotService.listSessions()
      .pipe(take(1))
      .subscribe({
        next: (sessions) => this.history.set(sessions.map(this.toChatSession)),
        error: (err: HttpErrorResponse) => {
          // Soft-fail: keep whatever history we had. No banner — history
          // is a convenience surface, not core chat functionality.
          console.warn('[chatbot-panel] list sessions failed:', err);
        },
      });
  }

  private toChatSession = (s: ChatbotSessionSummary): ChatSession => ({
    id: s.id,
    title: s.title ?? null,
    startedAt: new Date(s.createdAt),
    // Messages array deliberately empty — the listing endpoint doesn't
    // ship them. sessionPreview() prefers title when present and falls
    // back to "Empty session" otherwise.
    messages: [],
  });

  private sendCurrentRequest(content: string): void {
    this.isLoading.set(true);
    this.error.set(null);

    const ctx = this.pageContext.currentContext();
    // Include the prior turns (excluding the just-pushed user bubble at the
    // end) so the planner sees the conversation context — required when
    // resuming an older session whose in-memory state has expired, and good
    // hygiene for fresh sessions too. Backend caps at MaxHistoryMessages = 8,
    // so we trim to 8 here to keep the wire payload light.
    const priorMessages = this.messages().slice(0, -1).slice(-8);
    const history = priorMessages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }));

    const request: ChatbotMessageRequest = {
      message: content,
      conversationId: this.conversationId,
      history,
      module: ctx?.module ?? null,
      route: ctx?.route ?? null,
      portalKind: 'admin',
      entityContext: ctx?.entityContext ?? null,
      clientContext: ctx ? {
        contextVariant: (ctx.variant === 'list' || ctx.variant === 'detail') ? ctx.variant : null,
        launcherSource: 'header',
      } : null,
    };

    this.chatbotService
      .sendMessage(request)
      .pipe(take(1), finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => this.handleResponse(response),
        error: (err: HttpErrorResponse) => this.handleError(err),
      });
  }

  private handleResponse(response: ChatbotMessageResponse): void {
    this.conversationId = response.conversationId;
    this.lastRequest = null;

    const navigation: ChatNavigation[] | null = response.navigation
      ?.map(n => ({
        label: n.label,
        route: n.route,
        requiresModule: n.requiresModule ?? null,
        kind: n.kind ?? null,
        prefill: n.prefill ?? null,
        prompt: n.prompt ?? null,
      })) ?? null;

    const assistantMessage: ChatMessage = {
      // Backend-stamped id is the authoritative key for PATCH /selections.
      // Fallback to a fresh UUID is defensive — shouldn't fire in practice.
      id: response.message.id ?? crypto.randomUUID(),
      role: 'assistant',
      content: response.message.content,
      timestamp: new Date(),
      citations: response.citations?.map(c => ({
        title: c.title,
        sourceFile: c.sourceFile ?? null,
        section: c.section ?? null,
        sourcePath: c.sourcePath ?? null,
      })) ?? null,
      navigation,
      suggestions: response.suggestions?.map(label => ({ label, actionKey: null })) ?? null,
      slotFillGroups: response.slotFillGroups?.map(g => ({
        fieldKey: g.fieldKey,
        label: g.label,
        selectMode: g.selectMode,
        options: g.options.map(o => ({ label: o.label, value: o.value })),
      })) ?? null,
      toolRequired: response.toolRequired,
    };
    this.messages.update(prev => [...prev, assistantMessage]);
    // Refresh history so the current session updates its position/title
    // and freshly-pruned sessions disappear from the list.
    this.refreshHistory();
    // The turn burned credits and (on the first turn) minted a session —
    // refresh the meter so it tracks live.
    this.fetchUsage();
  }

  private handleError(err: HttpErrorResponse): void {
    // 403 with "FORBIDDEN: ..." surfaces the AI-disabled kill-switch
    // message; everything else is a generic network/backend failure.
    // The error banner exposes a Retry button (onRetryRequested) that
    // re-submits the same user message via lastRequest.
    const message = err.status === 403 && typeof err.error?.error === 'string'
      ? err.error.error
      : 'Sorry — I couldn\'t process that. Please retry.';
    this.error.set(message);
    console.error('[chatbot-panel] send failed:', err);
  }
}
